
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LevelConfig } from '@/models/LearnTypes';
import { CheckCircle, Info, Sparkles, SkipForward } from 'lucide-react';

interface LevelIntroProps {
  level: LevelConfig;
  levelIndex: number;
  onStartLevel: () => void;
  onSkipLevel?: () => void;
  isPreviouslyCompleted?: boolean;
  adjustedXpReward?: number;
}

const LevelIntro = ({ 
  level, 
  levelIndex, 
  onStartLevel, 
  onSkipLevel,
  isPreviouslyCompleted = false,
  adjustedXpReward
}: LevelIntroProps) => {
  // Use the adjusted XP if provided, otherwise fall back to the level's XP reward
  const xpReward = adjustedXpReward !== undefined ? adjustedXpReward : level.xpReward;
  
  return (
    <div className="animate-fade-in space-y-6">
      <Card className="border-none shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Level {levelIndex + 1}</CardTitle>
          <CardDescription className="text-lg">
            {level.title}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-8">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            {level.type === 'flashcard' && <span className="text-3xl">🗃️</span>}
            {level.type === 'reverse-flashcard' && <span className="text-3xl">🔄</span>}
            {level.type === 'spelling' && <span className="text-3xl">✏️</span>}
            {level.type === 'match' && <span className="text-3xl">🔀</span>}
          </div>
          
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">{level.description}</h3>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 text-yellow-400" />
              <span>Earn up to {xpReward} XP</span>
            </div>
            
            {isPreviouslyCompleted && (
              <div className="flex items-center gap-2 mt-4 text-muted-foreground bg-muted/50 px-4 py-2 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">You've completed this level before</span>
              </div>
            )}
          </div>
          
          <div className="w-full space-y-3">
            {/* Only show Skip Level button if the level was previously completed */}
            {onSkipLevel && isPreviouslyCompleted && (
              <Button 
                variant="outline" 
                className="w-full flex items-center gap-2" 
                onClick={onSkipLevel}
              >
                <SkipForward className="h-4 w-4" />
                Skip Level
              </Button>
            )}
            
            <Button 
              className="w-full" 
              size="lg"
              onClick={onStartLevel}
            >
              Start Level
            </Button>
          </div>
          
          <div className="w-full p-4 bg-muted/30 rounded-lg flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              {level.type === 'flashcard' && "You'll see French words and need to recall their English translations."}
              {level.type === 'reverse-flashcard' && "You'll see English words and need to recall their French translations."}
              {level.type === 'spelling' && "You'll need to type the correct spelling of French words."}
              {level.type === 'match' && "Match French words with their translation."}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LevelIntro;
